import React from "react";
import Modal from "Utils/Modal";
import service from "Api/service";
import { standardService } from "Api/service";
import { isValidToken } from "Api";

/** Hosted form + CVV help: wider layout uses less vertical space; shrinks on small viewports. */
const HOSTED_IFRAME_BLOCK_SIZE = "min(600px, max(400px, calc(100vh - 180px)))";

class WorldpayHostedPaymentModal extends React.Component {
  static defaultProps = {
    guestPayment: false,
  };

  iframeRef = React.createRef();

  /** Prevents duplicate POST / credit_card save when postMessage + polling both fire */
  _cardSaveStarted = false;

  /**
   * Monotonic id assigned when hosted setup succeeds; cleared on close/cancel/unmount.
   * In-flight poll callbacks compare against the id captured at poll start so they cannot
   * save after the modal invalidates the session (avoids duplicate save vs postMessage).
   */
  _worldpayFlowSeq = 0;
  _activeWorldpayFlowId = null;

  pollIntervalId = null;

  state = {
    loading: false,
    error: null,
    iframeUrl: null,
    referenceNumber: null,
    transactionSetupId: null,
    success: false,
    savingCard: false,
  };

  componentDidMount() {
    if (this.props.open) {
      this.initializeHostedPayment();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.open && !prevProps.open) {
      // Fresh attempt: do not show AVS/CVV errors from a previous session before setup returns
      this.stopPaymentStatusPolling();
      window.removeEventListener("message", this.handleWorldpayMessage);
      this._activeWorldpayFlowId = null;
      this._cardSaveStarted = false;
      this.setState({
        error: null,
        success: false,
        savingCard: false,
        iframeUrl: null,
        referenceNumber: null,
        transactionSetupId: null,
      });
      this.initializeHostedPayment();
    }
    if (!this.props.open && prevProps.open) {
      this.stopPaymentStatusPolling();
      this._activeWorldpayFlowId = null;
      this._cardSaveStarted = false;
      window.removeEventListener("message", this.handleWorldpayMessage);
      // Full reset so reopening never shows stale banner or polls old setup id
      this.setState({
        iframeUrl: null,
        error: null,
        loading: false,
        success: false,
        savingCard: false,
        referenceNumber: null,
        transactionSetupId: null,
      });
    }

    if (
      this.state.iframeUrl &&
      this.state.transactionSetupId &&
      (!prevState.iframeUrl || prevState.transactionSetupId !== this.state.transactionSetupId)
    ) {
      this.startPaymentStatusPolling();
    }
    if (!this.state.iframeUrl && prevState.iframeUrl) {
      this.stopPaymentStatusPolling();
    }
  }

  stopPaymentStatusPolling = () => {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  };

  /** Authenticated portal users only — payment_status requires customer auth */
  startPaymentStatusPolling = () => {
    if (this.pollIntervalId || !isValidToken()) return;

    const pollOnce = async () => {
      if (this._cardSaveStarted) return;

      const flowIdAtStart = this._activeWorldpayFlowId;
      if (flowIdAtStart == null) return;

      const { transactionSetupId } = this.state;
      if (!transactionSetupId) return;

      try {
        const axiosResp = await service.get(
          `${process.env.REACT_APP_API_DOMAIN}/api/portal/worldpay/payment_status/${transactionSetupId}`
        );
        const body = axiosResp?.data ?? axiosResp;

        if (
          !this.props.open ||
          this._activeWorldpayFlowId !== flowIdAtStart
        ) {
          return;
        }

        if (body.verification_failed && body.error) {
          this.stopPaymentStatusPolling();
          this.setState({ error: body.error, loading: false });
          if (this.props.onError) this.props.onError(body.error);
          return;
        }

        if (
          body.completed &&
          (body.transaction_id || body.payment_account_id)
        ) {
          this.stopPaymentStatusPolling();
          this.handlePaymentSuccess({
            transaction_id: body.transaction_id,
            payment_account_id: body.payment_account_id,
            ticket_number: body.ticket_number,
            last_four: body.last_four,
            transaction_setup_id: this.state.transactionSetupId,
          });
        }
      } catch {
        // ignore until session / network recover
      }
    };

    this.pollIntervalId = setInterval(pollOnce, 2000);
    pollOnce();
  };

  initializeHostedPayment = async () => {
    const {
      location,
      customerId,
      customerType,
      onCardSaved,
      onError,
    } = this.props;

    this.setState({ loading: true, error: null });
    this._cardSaveStarted = false;
    this._activeWorldpayFlowId = null;

    try {
      // Use standardService so 401 does not redirect the page (guest checkout has no auth).
      // Keeps the modal open and shows the iframe or an error instead of sending user to /login.
      const response = await standardService.post(
        `${process.env.REACT_APP_API_DOMAIN}/api/portal/worldpay/hosted_payment_setup`,
        {
          location_id: location.id,
          customer_id: customerId,
          customer_type: customerType,
        }
      );

      if (response.data.redirect_url) {
        // Use iframe for embedded hosted payment
        console.log("Setting up Worldpay payment iframe with URL:", response.data.redirect_url);

        this._worldpayFlowSeq += 1;
        this._activeWorldpayFlowId = this._worldpayFlowSeq;

        this.setState({
          loading: false,
          iframeUrl: response.data.redirect_url,
          referenceNumber: response.data.reference_number,
          transactionSetupId: response.data.transaction_setup_id,
        });

        // Listen for messages from the iframe (return page will post messages)
        window.addEventListener("message", this.handleWorldpayMessage);
      } else {
        throw new Error("No redirect URL received from Worldpay");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to initialize payment form";
      this.setState({ error: errorMessage, loading: false });
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  handleWorldpayMessage = (event) => {
    // Verify origin for security - accept messages from our API domain
    const apiDomain = process.env.REACT_APP_API_DOMAIN?.replace(/^https?:\/\//, "") || "";
    const allowedOrigins = [
      window.location.origin,
      apiDomain,
      `https://${apiDomain}`,
      `http://${apiDomain}`,
    ].filter(Boolean);

    const { data, origin } = event;

    // Debug logging
    console.log("Worldpay message received:", { data, origin, allowedOrigins });

    // Handle Worldpay callback - the return page will post a message
    if (data && typeof data === "object") {
      // For PaymentAccountCreate (card storage), Worldpay returns payment_account_id, not transaction_id
      if (
        data.type === "worldpay_payment_success" ||
        (data.success === true && (data.transaction_id || data.payment_account_id))
      ) {
        console.log("Processing payment success:", data);
        this.handlePaymentSuccess(data);
      } else if (data.type === "worldpay_payment_error" || data.success === false) {
        const errorMessage = data.error || "Payment failed";
        console.error("Payment error:", errorMessage);
        this.setState({ error: errorMessage, loading: false });
        if (this.props.onError) {
          this.props.onError(errorMessage);
        }
      } else if (data.type === "worldpay_payment_cancel" || data.cancelled) {
        console.log("Payment cancelled");
        this.handlePaymentCancel();
      } else {
        console.log("Unhandled message format:", data);
      }
    } else {
      console.log("Invalid message format:", data);
    }
  };

  handleIframeLoad = () => {
    // Check if the iframe has loaded a return page
    // Worldpay will redirect the iframe to our return URL with query string parameters
    console.log("Iframe loaded event fired");
    try {
      const iframe = this.iframeRef?.current;
      if (iframe && iframe.contentWindow) {
        // Try to access the iframe's location (may fail due to CORS if on different domain)
        // If it's our return page, it should post a message or we can parse the URL
        try {
          const iframeUrl = iframe.contentWindow.location.href;
          console.log("Iframe URL:", iframeUrl);
          // If the iframe has redirected to our return URL, parse the query string
          const isPortalReturn = iframeUrl.includes("/api/portal/worldpay/payment_return");
          const isTapgoodsReturn = iframeUrl.includes("/api/worldpay/payment_return");
          if (isPortalReturn || isTapgoodsReturn) {
            console.log("Detected payment_return URL in iframe");
            const url = new URL(iframeUrl);
            const params = new URLSearchParams(url.search);

            // Extract Worldpay response parameters
            const transactionId = params.get("TransactionID");
            const paymentAccountId = params.get("PaymentAccountID");
            const expressResponseCode = params.get("ExpressResponseCode");

            console.log("Payment return params:", {
              transactionId,
              paymentAccountId,
              expressResponseCode,
            });

            if (expressResponseCode === "0" && (transactionId || paymentAccountId)) {
              this.handlePaymentSuccess({
                transaction_id: transactionId,
                payment_account_id: paymentAccountId,
                express_response_code: expressResponseCode,
                transaction_setup_id:
                  params.get("TransactionSetupID") || this.state.transactionSetupId,
              });
            } else if (expressResponseCode && expressResponseCode !== "0") {
              const errorMsg = params.get("ExpressResponseMessage") || "Transaction failed";
              this.setState({ error: errorMsg, loading: false });
            }
          }
        } catch (e) {
          // CORS - can't access iframe URL if on different domain
          // This is expected when iframe is on Worldpay's domain
          console.log("CORS error accessing iframe URL (expected):", e.message);
          // In this case, we rely on the return page posting a message
        }
      }
    } catch (e) {
      // CORS - expected if iframe is on different domain
      console.log("Error in handleIframeLoad:", e.message);
    }
  };

  handlePaymentSuccess = async (data) => {
    if (!this.props.open || this._activeWorldpayFlowId == null) return;
    if (this._cardSaveStarted) return;
    this._cardSaveStarted = true;

    const { location, customerId, customerType, nickname, onCardSaved, onError } = this.props;

    this.setState({ savingCard: true, loading: false, success: false });

    try {
      // Get location_id from data (returned from Worldpay) or from props
      const locationId = data.location_id || data.locationId || location?.id;
      
      if (!locationId) {
        throw new Error("Location ID is required but was not provided");
      }

      const setupId =
        data.transaction_setup_id ||
        data.transactionSetupId ||
        this.state.transactionSetupId;
      if (
        (data.transaction_id || data.transactionId) &&
        !setupId
      ) {
        throw new Error("Missing payment session id — please close the form and try again.");
      }

      const cardSaveClient = isValidToken() ? service : standardService;
      const creditCardPayload = {
        customer_id: customerId,
        owner_type: customerType,
        nickname: (nickname && nickname.trim()) || "New Card",
        payment_account_id: data.payment_account_id || data.paymentAccountId,
        worldpay_transaction_id: data.transaction_id || data.transactionId,
        worldpay_ticket_number: data.ticket_number || data.ticketNumber,
        last_four: data.last_four || data.lastFour,
        guest_payment: this.props.guestPayment === true,
      };
      if (setupId) {
        creditCardPayload.worldpay_transaction_setup_id = setupId;
      }

      const response = await cardSaveClient.post(
        `${process.env.REACT_APP_API_DOMAIN}/api/portal/credit_cards`,
        {
          location_id: locationId,
          credit_card: creditCardPayload,
        }
      );

      // API may return { creditCard } or the card object at root
      const card = response.data?.creditCard || response.data;

      this.setState({ success: true, savingCard: false, iframeUrl: null });
      window.removeEventListener("message", this.handleWorldpayMessage);

      // Notify parent so it can add the card to session and close this modal. Short delay so user sees success.
      setTimeout(() => {
        if (onCardSaved && card) {
          onCardSaved(card);
        }
      }, 1200);
    } catch (error) {
      this._cardSaveStarted = false;
      const data = error.response?.data;
      const fieldErrors = data?.errors;
      let errorMessage;
      if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
        const msgs = Object.values(fieldErrors)
          .flat()
          .map(String)
          .filter(Boolean);
        if (msgs.length) errorMessage = msgs.join(" ");
      }
      if (!errorMessage && data?.message) errorMessage = data.message;
      if (!errorMessage && typeof data?.error === "string" && data.error !== "Validation failed") {
        errorMessage = data.error;
      }
      errorMessage = errorMessage || error.message || "Failed to save credit card";
      this.setState({ error: errorMessage, savingCard: false, loading: false });
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  handlePaymentCancel = () => {
    this.stopPaymentStatusPolling();
    this._activeWorldpayFlowId = null;
    this._cardSaveStarted = false;
    window.removeEventListener("message", this.handleWorldpayMessage);
    this.setState({ iframeUrl: null, loading: false, error: null });
    if (this.props.toggle) {
      this.props.toggle();
    }
  };

  componentWillUnmount() {
    this.stopPaymentStatusPolling();
    this._activeWorldpayFlowId = null;
    this._cardSaveStarted = false;
    window.removeEventListener("message", this.handleWorldpayMessage);
  }

  render() {
    const { open, toggle, title } = this.props;
    const { loading, error, iframeUrl, savingCard, success } = this.state;

    return (
      <Modal
        open={open}
        toggle={toggle}
        title={title || "Add Credit Card"}
        large={true}
        className="worldpay-hosted-modal"
        actions={[
          <button
            key="cancel"
            className="btn cancel"
            onClick={toggle}
            style={{
              height: "30px",
              minHeight: "30px",
              lineHeight: "30px",
              padding: "0 12px",
            }}
          >
            Cancel
          </button>,
        ]}
      >
        <div
          style={{
            width: "100%",
            minHeight: HOSTED_IFRAME_BLOCK_SIZE,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          {loading && !iframeUrl && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <p>Loading secure payment form...</p>
            </div>
          )}

          {success && (
            <div
              style={{
                padding: "32px",
                textAlign: "center",
                width: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: "#4caf50",
                  marginBottom: "16px",
                }}
              >
                ✓
              </div>
              <h3 style={{ color: "#4caf50", marginBottom: "8px" }}>
                Card Saved Successfully!
              </h3>
              <p style={{ color: "#666" }}>Your credit card has been securely stored.</p>
            </div>
          )}

          {savingCard && !success && (
            <div
              style={{
                padding: "32px",
                textAlign: "center",
                width: "100%",
              }}
            >
              <p style={{ fontSize: "18px", marginBottom: "8px" }}>Saving your card...</p>
              <p style={{ fontSize: "14px", color: "#666" }}>
                Please wait while we securely store your payment information.
              </p>
            </div>
          )}

          {error && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fee",
                color: "#c33",
                borderRadius: "4px",
                margin: "16px 0",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {iframeUrl && !savingCard && !success && !error && (
            <iframe
              ref={this.iframeRef}
              src={iframeUrl}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{
                width: "100%",
                minHeight: HOSTED_IFRAME_BLOCK_SIZE,
                height: HOSTED_IFRAME_BLOCK_SIZE,
                border: "none",
                display: "block",
                // The hosted form's labels are navy, chosen for a light background. If its document
                // paints no background of its own the iframe is transparent and the modal panel
                // shows through, which made the field names unreadable on the dark themes
                // (TD1-10436). The API now sends an opaque body in its CustomCss, but that is
                // Worldpay's to honour; painting the iframe element too keeps the form legible even
                // if they drop it. Invisible on the light themes, whose panel is already white.
                backgroundColor: "#fff",
              }}
              title="Worldpay Secure Payment Form"
              onLoad={this.handleIframeLoad}
            />
          )}
        </div>
      </Modal>
    );
  }
}

export default WorldpayHostedPaymentModal;
